"use client";

import { FC, useEffect, useRef, useState } from "react";

// Hata mesajına gömülü süre: "00:01:54" (sa:dk:sn) veya "01:54" (dk:sn).
// API örneği: "Yeni bir sms göndermek için 00:01:54 saniye sonra tekrar deneyiniz."
const TIME_REGEX = /(\d{1,2}:\d{2}(?::\d{2})?)/;

function parseTimeToSeconds(timeStr: string): number {
  const parts = timeStr.split(":").map(Number);
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  return 0;
}

// Kaynak formatı korunur: mesaj "00:01:54" ile geldiyse geri sayım da
// üç parçalı basılır, "01:54" ile geldiyse iki parçalı.
function formatSeconds(totalSeconds: number, withHours: boolean): string {
  const hours = Math.floor(totalSeconds / 3600);
  const mins = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;
  const mm = String(mins).padStart(2, "0");
  const ss = String(secs).padStart(2, "0");
  return withHours ? `${String(hours).padStart(2, "0")}:${mm}:${ss}` : `${mm}:${ss}`;
}

interface CountdownErrorProps {
  error?: string | null;
  /** Süre dolunca çağrılır — mesajı kaldırmak için (ör. dispatch(clearError())). */
  onClear?: () => void;
  className?: string;
}

/**
 * Hata mesajını basar; içinde süre varsa saniye saniye geriye sayar ve süre
 * bitince onClear ile mesajın kaldırılmasını ister. Süre yoksa mesajı olduğu
 * gibi gösterir.
 */
const CountdownError: FC<CountdownErrorProps> = ({
  error,
  onClear,
  className = "text-sm text-red-600 text-center",
}) => {
  const onClearRef = useRef(onClear);
  useEffect(() => {
    onClearRef.current = onClear;
  }, [onClear]);

  const [remaining, setRemaining] = useState<number | null>(null);

  useEffect(() => {
    const match = error ? error.match(TIME_REGEX) : null;
    const initial = match ? parseTimeToSeconds(match[1]) : 0;

    if (!match || initial <= 0) {
      setRemaining(null);
      return;
    }

    // Kalan süre closure'da takip edilir — ref gerekmez, StrictMode'da güvenli.
    let left = initial;
    setRemaining(left);

    const id = setInterval(() => {
      left -= 1;
      if (left <= 0) {
        clearInterval(id);
        setRemaining(null);
        onClearRef.current?.();
      } else {
        setRemaining(left);
      }
    }, 1000);

    return () => clearInterval(id);
  }, [error]);

  if (!error) return null;

  const match = error.match(TIME_REGEX);
  const withHours = match ? match[1].split(":").length === 3 : false;

  return (
    <div className={className}>
      {remaining !== null
        ? error.replace(TIME_REGEX, formatSeconds(remaining, withHours))
        : error}
    </div>
  );
};

export default CountdownError;
